import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { clearSegmentOutLegacyCache, refreshSegmentOutLegacyCache } from './cache';

/**
 * Legacy Segment source writes: save the event selection (PUT), remove the
 * connector (DELETE).
 *
 * There is no connect here. The `segdotcom` PUT does create a record, but nothing
 * in the product offers that any more — connecting goes through the current
 * generation's form, which sets `use_javascript`. This pair exists to configure
 * and to retire the records that predate it.
 */

/* ------------------------------------------------------------------------- */
/* saveSegmentOutLegacy                                                      */
/* ------------------------------------------------------------------------- */

export interface SaveSegmentOutLegacyInput {
  workspaceId: number;
  /**
   * The key the record already holds, sent straight back.
   *
   * Not decorative: the endpoint rewrites the key from whatever the body carries, on
   * an update as much as a create, so omitting it or sending a placeholder would
   * replace the workspace's Segment credential.
   */
  key: string;
  /** The whole catalogue with each event's state, as the read answered it. */
  events: Record<string, boolean>;
  /**
   * The workspace's Segment region, resent unchanged.
   *
   * Required, and not optional as it looks: the endpoint accepts a create with
   * `key` alone but rejects an **update** that omits `events` or `eu`. The legacy
   * read does not carry it, so the caller takes it off the `segment_out` record —
   * the same record whose `use_javascript` chose this form.
   */
  eu: boolean;
}

/**
 * @public Save the legacy connector's event selection.
 *
 * Ember sent `{key, events}` from this form and no `eu`, which the endpoint answers
 * with `422 "eu is required"` — verified against production. So the legacy save has
 * never worked for a record that exists, which is of a piece with the rest of this
 * path: nothing could reach it to find out.
 *
 * `enabled` is deliberately absent, and the endpoint preserves it. The legacy
 * settings block has no enable flag, so this write never touches the record's
 * disabled state — measured on production by disabling a record through the
 * `segment_out` endpoint, saving through this one, and reading it back still
 * disabled. The browser test asserts the exact body, which is what pins the
 * omission on this side.
 */
export async function saveSegmentOutLegacy({ workspaceId, key, events, eu }: SaveSegmentOutLegacyInput): Promise<void> {
  await hydraUiApi.integrations.updateSegdotcom({
    params: { path: { environment_id: String(workspaceId) } },
    body: { key, events, eu },
  });
}

export type UseSaveSegmentOutLegacyVariables = Omit<SaveSegmentOutLegacyInput, 'workspaceId'>;

type SaveOptions = {
  /**
   * The workspace to write to, resolved by the caller.
   *
   * Passed in rather than parsed from the URL: these hooks run during render, and
   * a page can render under `/workspaces/last` before the shell rewrites it, or
   * under a workspace segment the reader cannot access — where the resolved
   * workspace and the URL disagree. Taking it from the caller keeps the read, the
   * write, and the cache slot on one id.
   */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, UseSaveSegmentOutLegacyVariables>;
};

/** @public */
export const useSaveSegmentOutLegacy = ({ workspaceId, mutationConfig }: SaveOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseSaveSegmentOutLegacyVariables) => saveSegmentOutLegacy({ ...input, workspaceId }),
    onSuccess: (result, ...args) => {
      void refreshSegmentOutLegacyCache(qc, workspaceId);
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};

/* ------------------------------------------------------------------------- */
/* removeSegmentOutLegacy                                                    */
/* ------------------------------------------------------------------------- */

export interface RemoveSegmentOutLegacyInput {
  workspaceId: number;
}

/** @public Remove the legacy connector. */
export async function removeSegmentOutLegacy({ workspaceId }: RemoveSegmentOutLegacyInput): Promise<void> {
  await hydraUiApi.integrations.deleteSegdotcom({
    params: { path: { environment_id: String(workspaceId) } },
  });
}

type RemoveOptions = {
  /** The workspace to write to, resolved by the caller — see the save hook above. */
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, void>;
};

/** @public */
export const useRemoveSegmentOutLegacy = ({ workspaceId, mutationConfig }: RemoveOptions) => {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: () => removeSegmentOutLegacy({ workspaceId }),
    onSuccess: (result, ...args) => {
      clearSegmentOutLegacyCache(qc, workspaceId);
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};
